import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { TripLeg } from '../../../data/models/reservation.model';
import * as reservationSlice from '../../../data/reservation.slice';
import * as uiSlice from '../../../data/ui.slice';
import TextInput from '../../forms/TextInput';

interface ReopenTripLegDialogProps {
  tripLeg: TripLeg;
  open: boolean;
  close: () => void;
}

const PREFIX = 'ReopenTripLegDialog';

const classes = {
  content: `${PREFIX}-content`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'row',
    minWidth: 500,
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

export default function ReopenTripLegDialog({
  tripLeg,
  open,
  close,
}: ReopenTripLegDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const loadedReopen = useAppSelector(
    reservationSlice.selectLoadedReopenRejectedTripLeg,
  );
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);

  React.useEffect(() => {
    if (saveSuccessMessage && loadedReopen) {
      handleClose();
      dispatch(reservationSlice.setLoadedReopenRejectedTripLeg(false));
    }
  }, [saveSuccessMessage, loadedReopen]);

  const formHook = useForm({
    defaultValues: {
      tripLegReferenceID: tripLeg.tripLegReferenceID,
      passengerUUID: tripLeg.passenger?.passengerUUID,
    },
  });

  const handleClose = () => {
    close();
  };

  const handleSubmit = () => {
    if (!tripLeg.tripLegReferenceID) return;
    dispatch(
      reservationSlice.reopenRejectedTripLeg(
        tripLeg.passengerUUID!,
        tripLeg.tripLegReferenceID,
      ),
    );
  };

  return (
    <>
      <RootDialog open={open} onClose={handleClose} maxWidth={'xl'}>
        <DialogTitle>{t('triplegInventory.reopenTitle')}</DialogTitle>
        <DialogContent>
          <Box className={classes.content}>
            <TextInput
              name="tripLegReferenceID"
              label={t('triplegInventory.tripId')}
              control={formHook.control}
              required={true}
              disabled={true}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            {t('common.reopen')}
          </Button>
        </DialogActions>
      </RootDialog>
    </>
  );
}
